import { components } from 'react-select';
import { User, UserEquipment } from '../../clients/quipli-service/user/user';
import Icon from '../../components/icons/icon';
import { AsyncCreatableSelect } from '../../components/select-form/select-form';
import { Typography } from '../../components/typography/typography';
import { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import classNames from 'classnames';
import { useUserAPI } from '../../clients/quipli-service/user/user-api';
import { UserEquipmentForm } from './equipment-form';
import { truncateString } from '../../util/util';

export const CustomerEquipmentSelect = ({
  user,
  setUserEquipment,
}: {
  user: User | null;
  setUserEquipment: (c: UserEquipment | null) => void;
}) => {
  const userAPI = useUserAPI();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [userKey, setUserKey] = useState<string>('');
  const [selectedUserEquipment, setSelectedUserEquipment] = useState<UserEquipment | null>(null);

  useEffect(() => {
    if (!user) {
      setUserKey('');
    } else {
      setUserKey(`${user.id}`);
    }
  }, [user]);

  // Callbacks
  const debouncedSearch = debounce((keyword: string, callback) => {
    if (!user) {
      callback([]);
    }

    userAPI
      .searchUserEquipment({
        userId: user!.id,
        keyword,
        pageNumber: 1,
        pageSize: 30,
      })
      .then((res) => callback(res.items));
  }, 300);

  const onUserEquipmentSelected = (userEquipment: UserEquipment | null) => {
    setSelectedUserEquipment(userEquipment);
    setUserEquipment(userEquipment);
  };

  const formatOptionLabel = (userEquipment: UserEquipment) => {
    const label = userEquipment.make ? `${userEquipment.make} ${userEquipment.model}` : `${userEquipment.description}`;
    return truncateString(label, 30);
  };

  return (
    <>
      <AsyncCreatableSelect<UserEquipment>
        key={userKey}
        cacheOptions
        defaultOptions
        loadOptions={debouncedSearch}
        onClickCreatableOption={() => setIsModalOpen(true)}
        getOptionLabel={formatOptionLabel}
        getOptionValue={(op) => `${op.id}`}
        onChange={(value) => onUserEquipmentSelected(value)}
        loadingMessage={() => 'Loading customer equipment...'}
        noOptionsMessage={() => 'No equipment found'}
        dataCy="customer-equipment-select"
        value={selectedUserEquipment}
        isDisabled={!user}
        components={{
          Placeholder: (props) => (
            <components.Placeholder {...props}>
              <Typography type="text-sm">Search equipment</Typography>
            </components.Placeholder>
          ),
          ValueContainer: (props) => (
            <div className="flex items-center gap-[6px] pl-[10px]">
              <Icon icon="Search" size="medium" stroke="var(--grey-500)" />
              <components.ValueContainer {...props} />
            </div>
          ),
          Option: (props) => {
            //@ts-expect-error __isNew__
            const isCreateOption = props.data.__isNew__ === true;

            return (
              <components.Option {...props}>
                <div
                  className={classNames('flex items-center gap-[6px]', {
                    'justify-between': !isCreateOption,
                  })}
                >
                  {isCreateOption ? (
                    <>
                      <div className="quipli-v2 flex items-center">
                        <Icon icon="Plus" stroke="var(--quipli-black-800)" />
                      </div>
                      <Typography dataCy="add-equipment" type="text-md" weight="medium" className="text-grey-900">
                        Add new equipment
                      </Typography>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-[4px]">
                        <Typography type="text-md" weight="medium" className="text-grey-900">
                          {props.children}
                        </Typography>
                        <div>
                          {props.data.make ? (
                            <>
                              <Typography type="text-sm" className="text-grey-500">
                                {truncateString(props.data.make, 40)}
                              </Typography>
                              <Typography type="text-sm" className="text-grey-500">
                                {truncateString(props.data.model, 40)}
                              </Typography>
                            </>
                          ) : (
                            <Typography type="text-sm" className="text-grey-500">
                              {truncateString(props.data.description, 40)}
                            </Typography>
                          )}

                          <Typography type="text-sm" className="text-grey-500">
                            {truncateString(props.data.serialNumber, 40)}
                          </Typography>
                        </div>
                      </div>
                      {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
                    </>
                  )}
                </div>
              </components.Option>
            );
          },
        }}
      />

      {user && (
        <UserEquipmentForm
          userId={user.id}
          isOpen={isModalOpen}
          close={() => setIsModalOpen(false)}
          setNewUserEquipment={onUserEquipmentSelected}
        />
      )}
    </>
  );
};
